import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import {
  Button,
  CreateCompanyModal,
  FileUploadModal,
  FormWrapper,
  ImageUploadModal,
  LinkUploadModal,
  SubmitButton,
  ToggleButton,
  ViewMediaModal,
} from "../../components";
import { emiOptions, FileTypes, projectTypeOptions } from "../../constants";
import { useCompany, useProject } from "../../hooks";
import { formatDate } from "../../services/utils";
import ImageSliderModal from "../modals/ImageSliderModal";

const ProjectForm = ({ id, onNextStep, isCurrentStep }) => {
  // handle form section view
  const [showForm, setShowForm] = useState(isCurrentStep);
  const [showDocumentsSection, setShowDocumentsSection] = useState(true);
  const [showDescriptionSection, setShowDescriptionSection] = useState(true);
  useEffect(() => setShowForm(isCurrentStep), [isCurrentStep]);
  const { getAllCompaniesWithNameAndLogo } = useCompany();
  const { getAllProjectNames } = useProject();

  const [companyOptions, setCompanyOptions] = useState([]); // options for select input
  const [projectNameList, setProjectNameList] = useState([]);

  // Handle Modal View
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [showLinkUploadModal, setShowLinkUploadModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [fileUploadOptions, setFileUploadOptions] = useState({});
  const [showViewMediaModal, setShowViewMediaModal] = useState(false);
  const [viewMediaOptions, setViewMediaOptions] = useState({});
  const [showImageSliderModal, setShowImageSliderModal] = useState(false);

  const [inputData, setInputData] = useState({
    project_name: "",
    state: "",
    city: "",
    gaon: "",
    taluka: "",
    // plot_area: "",
    short_location: "",
    zone: "",
    road_to_plot: "",
    // direction: "",
    // text_location: "",
    sanctioning_authority: "",
    start_range: "",
    end_range: "",
    token_percent: "",
    rera_number: "",
    development_charges: "",
    registration_charges: "",
    completion_eta: "",
    late_payment_interest: "",
    days_of_grace: "",
    cancellation_percent: "",
    description: "",
    plot_hold_limit: "",
    plot_area_min: "",
    plot_area_max: "",
  });
  const [selectData, setSelectData] = useState({
    company: undefined,
    project_type: undefined,
    emi_options: undefined,
  });
  const [uploads, setUploads] = useState({
    project_layout: "",
    slider_images: "",
    drone_video: "",
    brochure: "",
    masterfile_pdf: "",
    rera_certificate: "",
    google_maps_url: "",
  });
  const [urls, setUrls] = useState({
    project_layout: "",
    slider_images: "",
    drone_video: "",
    brochure: "",
    masterfile_pdf: "",
    rera_certificate: "",
  });

  const handleViewMedia = (title, mediaType, key) => {
    const savedData = JSON.parse(localStorage.getItem(id));
    const url = key === "company_logo" ? selectData.company.logo : savedData[`${key}_url`];
    setShowViewMediaModal(true);
    setViewMediaOptions({ title: title, url: key === "slider_images" ? url : [url], mediaType: mediaType });
  };

  const getProjectNames = async () => {
    const { projects, error } = await getAllProjectNames();
    if (error) return toast.error(error);
    setProjectNameList(projects);
  };
  const getCompanies = async () => {
    const { companies, error } = await getAllCompaniesWithNameAndLogo();
    if (error) return toast.error(error);
    setCompanyOptions(companies.map((company) => ({ ...company, value: company.id, label: company.name })));
  };

  useEffect(() => {
    if (isCurrentStep) {
      if (!companyOptions.length) getCompanies();
      if (!projectNameList.length) getProjectNames();
    }
  }, []);

  const [newCompany, setNewCompany] = useState("");
  const handleCloseCreateCompanyModal = async (companyData) => {
    setShowCreateCompanyModal(false);
    if (!companyData) return;
    setNewCompany({ ...companyData, value: companyData.id, label: companyData.name });
    localStorage.setItem("isNewCompany", true);
    await getCompanies();
    // const selectedCompany = companyOptions.find((option) => option.value === companyId);
    // setSelectData((prev) => ({ ...prev, company: selectedCompany }));
  };

  const handleSaveLink = (link) => {
    setShowLinkUploadModal(false);
    setUploads((prev) => ({ ...prev, google_maps_url: link }));
    const savedData = JSON.parse(localStorage.getItem(id));
    localStorage.setItem(id, JSON.stringify({ ...savedData, google_maps_url: link }));
  };
  const handleFileUpload = (imageId, url, key) => {
    setUploads((prev) => ({ ...prev, [key]: imageId }));
    setUrls((prev) => ({ ...prev, [key]: url }));
    const savedData = JSON.parse(localStorage.getItem(id));
    localStorage.setItem(id, JSON.stringify({ ...savedData, [key]: imageId, [`${key}_url`]: url }));
  };
  const handleProjectLayoutUpload = () => {
    setFileUploadOptions({
      title: "Upload Project Layout",
      handleUpload: (id, url) => handleFileUpload(id, url, "project_layout"),
    });
    setShowImageUploadModal(true);
  };
  const handleImageSliderUpload = () => {
    setFileUploadOptions({
      title: "Upload Image Slider",
      handleUpload: (id, url) => handleFileUpload(id, url, "slider_images"),
      // isMulti: true,
    });
    setShowImageSliderModal(true);
    // setShowImageUploadModal(true);
  };
  const handleDroneVideoUpload = () => {
    setFileUploadOptions({
      title: "Upload Drone Video",
      handleUpload: (id, url) => handleFileUpload(id, url, "drone_video"),
      fileType: FileTypes.DRONE_VIDEO,
    });
    setShowImageUploadModal(true);
  };
  const handleBrochureUpload = () => {
    setFileUploadOptions({
      title: "Upload Brochure",
      handleUpload: (id, url) => handleFileUpload(id, url, "brochure"),
      fileType: FileTypes.BROCHURE,
    });
    setShowFileUploadModal(true);
  };
  const handleMasterfileUpload = () => {
    setFileUploadOptions({
      title: "Upload Masterfile PDF",
      handleUpload: (id, url) => handleFileUpload(id, url, "masterfile_pdf"),
      fileType: FileTypes.MASTERFILE,
    });
    setShowFileUploadModal(true);
  };
  const handleReraCertificateUpload = () => {
    setFileUploadOptions({
      title: "Upload RERA Certificate",
      handleUpload: (id, url) => handleFileUpload(id, url, "rera_certificate"),
      fileType: FileTypes.RERA_CERTIFICATE,
    });
    setShowFileUploadModal(true);
  };

  const handleInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
    if (useTextCase === "capitalize_first") newValue = value.charAt(0).toUpperCase() + value.slice(1);
    if (useTextCase === "uppercase") newValue = value.toUpperCase();

    if (name === "project_name" && projectNameList.includes(value)) {
      toast.error("Project name already exists");
    }
    setInputData((prev) => ({ ...prev, [name]: newValue }));
    const savedData = JSON.parse(localStorage.getItem(id));
    localStorage.setItem(id, JSON.stringify({ ...savedData, [name]: newValue }));
  };
  const handleSelectChange = (value, name) => {
    setSelectData((prev) => ({ ...prev, [name]: value }));
    const savedData = JSON.parse(localStorage.getItem(id));
    localStorage.setItem(id, JSON.stringify({ ...savedData, [name]: value }));
  };

  // load saved data from local storage in case of reload
  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem(id));
    if (savedData) {
      Object.keys(inputData).forEach((key) => {
        if (key === "completion_eta") {
          const formattedDate = savedData[key]?.split("/").reverse().join("-");
          setInputData((prev) => ({ ...prev, [key]: formattedDate }));
        } else setInputData((prev) => ({ ...prev, [key]: savedData[key] }));
      });
      let selectValues = {};
      if (savedData.company) selectValues["company"] = savedData.company;
      if (savedData.project_type)
        selectValues["project_type"] = projectTypeOptions.find(
          (option) => option.value === savedData.project_type.value
        );
      if (savedData.emi_options)
        selectValues["emi_options"] = emiOptions.find((option) => option.value === savedData.emi_options.value);
      setSelectData(selectValues);
      Object.keys(uploads).forEach((key) => {
        setUploads((prev) => ({ ...prev, [key]: savedData[key] }));
      });
      Object.keys(urls).forEach((key) => {
        setUrls((prev) => ({ ...prev, [key]: savedData[`${key}_url`] }));
      });
    }
  }, []);

  const validateData = (data) => {
    let isValid = true;
    Object.keys(data).forEach((key) => {
      if (!data[key]) {
        toast.error(`Please enter a valid ${key.split("_").join(" ")}`);
        isValid = false;
        return false;
      }
    });
    return isValid;
  };

  const handleProjectFormSubmit = async (e) => {
    e.preventDefault();
    if (inputData.project_name && projectNameList.includes(inputData.project_name)) {
      toast.error("Project name already exists");
      return;
    }
    const formData = {
      ...inputData,
      ...uploads,
      completion_eta: formatDate(inputData?.completion_eta),
      project_type: selectData.project_type?.value,
      emi_options: +`${selectData.emi_options?.value}`,
      company_id: selectData.company?.value,
      plot_area_min: +inputData?.plot_area_min,
      plot_area_max: +inputData?.plot_area_max,
      days_of_grace: +inputData?.days_of_grace,
    };

    if (!validateData(formData)) return;
    onNextStep(formData);
  };

  return (
    <>
      <FormWrapper id="new-project-form" onSubmit={handleProjectFormSubmit}>
        <div className="section_title_wrapper">
          <h3 className="section_title">New Project Form</h3>
          <ToggleButton open={showForm} onClick={() => setShowForm((prev) => !prev)} />
        </div>
        <section className={`section_wrapper ${!showForm && "hide"}`}>
          <section className="section grid_3">
            <div className="column spread">
              <CreatableSelect
                name="company"
                required
                className="select"
                options={companyOptions}
                onChange={(newValue) => handleSelectChange(newValue, "company")}
                value={selectData.company}
                isDisabled={!isCurrentStep}
                onCreateOption={() => setShowCreateCompanyModal(true)}
                placeholder="Enter Company Name"
                createOptionPosition="first"
                isValidNewOption={() => true}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: "1px solid var(--gray-light)",
                    boxShadow: "none",
                    ":hover": {
                      border: "1px solid var(--gray-light)",
                    },
                  }),
                }}
              />
              <div className="row">
                <span className="label">Upload Company Logo</span>
                {selectData.company?.logo ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Company Logo", "image", "company_logo")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No Company Selected</span>
                )}
              </div>
              <input
                type="text"
                name="project_name"
                required
                className="input"
                placeholder="Enter project name"
                onChange={(e) => handleInputChange(e, "capitalize")}
                value={inputData.project_name}
                disabled={!isCurrentStep}
                pattern="([a-zA-Z0-9\s+]+)"
                title="Only alphabets are allowed"
                maxLength={50}
              />
              <input
                type="text"
                name="state"
                required
                onChange={(e) => handleInputChange(e, "capitalize")}
                value={inputData.state}
                placeholder="Enter State"
                className="input"
                disabled={!isCurrentStep}
                pattern="([a-zA-Z\s+]+)"
                title="Only alphabets are allowed"
                maxLength={50}
              />
              <input
                type="text"
                name="city"
                required
                onChange={(e) => handleInputChange(e, "capitalize")}
                value={inputData.city}
                placeholder="Enter City"
                className="input"
                disabled={!isCurrentStep}
                pattern="([a-zA-Z\s+]+)"
                title="Only alphabets are allowed"
                maxLength={50}
              />
              <input
                type="text"
                name="gaon"
                required
                onChange={(e) => handleInputChange(e, "capitalize")}
                value={inputData.gaon}
                placeholder="Enter Gaon"
                className="input"
                disabled={!isCurrentStep}
                pattern="([a-zA-Z\s+]+)"
                maxLength={50}
                title="Only alphabets are allowed"
              />
              <input
                type="text"
                name="taluka"
                required
                onChange={(e) => handleInputChange(e, "capitalize")}
                value={inputData.taluka}
                placeholder="Enter Taluka"
                className="input"
                disabled={!isCurrentStep}
                pattern="([a-zA-Z\s+]+)"
                title="Only alphabets are allowed"
                maxLength={50}
              />
              <div className="grid_3">
                <div className="input_with_suffix_wrapper">
                  <input
                    type="number"
                    name="plot_area_min"
                    required
                    onChange={handleInputChange}
                    value={inputData.plot_area_min}
                    className="input"
                    disabled={!isCurrentStep}
                    min={1}
                    max={inputData.plot_area_max}
                    placeholder="Min Area"
                  />
                  <span className="suffix">Sq. Ft.</span>
                </div>
                <span className="is-center label">Plot Area</span>
                <div className="input_with_suffix_wrapper">
                  <input
                    type="number"
                    name="plot_area_max"
                    required
                    onChange={handleInputChange}
                    value={inputData.plot_area_max}
                    className="input"
                    disabled={!isCurrentStep}
                    min={inputData.plot_area_min}
                    placeholder="Max Area"
                  />
                  <span className="suffix">Sq. Ft.</span>
                </div>
              </div>
              <input
                type="text"
                name="short_location"
                required
                onChange={(e) => handleInputChange(e, "capitalize_first")}
                value={inputData.short_location}
                placeholder="Enter Short Location"
                className="input"
                disabled={!isCurrentStep}
              />
              <input
                type="text"
                name="zone"
                required
                onChange={(e) => handleInputChange(e, "capitalize_first")}
                value={inputData.zone}
                placeholder="Enter Zone"
                className="input"
                disabled={!isCurrentStep}
              />
              <input
                type="text"
                name="road_to_plot"
                required
                className="input"
                placeholder="Enter Road to Plot"
                value={inputData.road_to_plot}
                onChange={(e) => handleInputChange(e, "capitalize_first")}
                disabled={!isCurrentStep}
              />
              {/* <input
                type="text"
                name="direction"
                required
                onChange={(e) => handleInputChange(e, "capitalize_first")}
                value={inputData.direction}
                placeholder="Enter Direction (Vaastu)"
                className="input"
                disabled={!isCurrentStep}
              /> */}
              {/* <input
                type="text"
                name="text_location"
                required
                onChange={(e) => handleInputChange(e, "capitalize_first")}
                value={inputData.text_location}
                placeholder="Enter Text Written Location"
                className="input"
                disabled={!isCurrentStep}
              /> */}
              <div className="row">
                <span className="label">Sanctioning Authority</span>
                <input
                  type="text"
                  name="sanctioning_authority"
                  required
                  onChange={(e) => handleInputChange(e, "uppercase")}
                  value={inputData.sanctioning_authority}
                  className="input full"
                  placeholder="Enter text"
                  disabled={!isCurrentStep}
                  pattern="([a-zA-Z\s+]+)"
                  title="Only alphabets are allowed"
                />
              </div>
              <div className="row">
                <span className="label">Enter Google Maps Location</span>
                {uploads.google_maps_url ? (
                  <Link target="_blank" to={uploads.google_maps_url} className="preview">
                    visit link
                  </Link>
                ) : (
                  <span className="small gray">No link added</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={() => setShowLinkUploadModal(true)}>
                  Upload Link
                </Button>
              </div>
              <div className="row">
                <span className="label">Select Type</span>
                <Select
                  name="project_type"
                  required
                  className="select"
                  options={projectTypeOptions}
                  value={selectData.project_type}
                  onChange={(newValue) => handleSelectChange(newValue, "project_type")}
                  isDisabled={!isCurrentStep}
                  placeholder="Select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: "1px solid var(--gray-light)",
                      boxShadow: "none",
                      ":hover": {
                        border: "1px solid var(--gray-light)",
                      },
                    }),
                  }}
                />
              </div>
            </div>
            <div className="vertical_divider"></div>
            <div className="column">
              <div className="row">
                <span className="label">Upload Project Layout</span>
                {urls.project_layout ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Project Layout", "image", "project_layout")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No Image Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleProjectLayoutUpload}>
                  Upload
                </Button>
              </div>
              <div className="row">
                <span className="label">Upload Image Slider</span>
                {urls.slider_images ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Image Slider", "image", "slider_images")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No Images Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleImageSliderUpload}>
                  Upload
                </Button>
              </div>
              <div className="row">
                <span className="label">Upload Drone Video</span>
                {urls.drone_video ? (
                  <Button className="ghost_gray" onClick={() => handleViewMedia("Drone Video", "video", "drone_video")}>
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No Video Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleDroneVideoUpload}>
                  Upload
                </Button>
              </div>
              <div className="grid_3">
                <div className="input_with_prefix_wrapper">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="start_range"
                    required
                    className="input"
                    onChange={handleInputChange}
                    value={inputData.start_range}
                    placeholder="Start Range"
                    disabled={!isCurrentStep}
                    max={inputData.end_range}
                    min={0}
                  />
                </div>
                <span className="label is-center">Rate Range (per Sq.ft.)</span>
                <div className="input_with_prefix_wrapper">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="end_range"
                    required
                    onChange={handleInputChange}
                    value={inputData.end_range}
                    className="input"
                    placeholder="End Range"
                    disabled={!isCurrentStep}
                    min={inputData.min_range}
                  />
                </div>
              </div>
              <div className="row">
                <span className="label">Select EMI Options</span>
                <Select
                  name="emi_options"
                  value={selectData.emi_options}
                  onChange={(newValue) => handleSelectChange(newValue, "emi_options")}
                  required
                  className="select"
                  options={emiOptions}
                  isDisabled={!isCurrentStep}
                  placeholder="Select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: "1px solid var(--gray-light)",
                      boxShadow: "none",
                      ":hover": {
                        border: "1px solid var(--gray-light)",
                      },
                    }),
                  }}
                />
              </div>
              <div className="row">
                <span className="label">Enter Token percent</span>
                <div className="input_with_suffix_wrapper">
                  <input
                    type="number"
                    name="token_percent"
                    required
                    className="input"
                    value={inputData.token_percent}
                    onChange={handleInputChange}
                    disabled={!isCurrentStep}
                    min={0}
                    max={100}
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
              <input
                type="text"
                name="rera_number"
                required
                className="input"
                placeholder="Enter Maha RERA number"
                value={inputData.rera_number}
                onChange={handleInputChange}
                disabled={!isCurrentStep}
              />
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  name="development_charges"
                  required
                  className="input"
                  placeholder="Enter Development Charges (Water & Electricity)"
                  value={inputData.development_charges}
                  onChange={handleInputChange}
                  disabled={!isCurrentStep}
                  min={0}
                />
              </div>
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  name="registration_charges"
                  required
                  className="input"
                  placeholder="Enter Registration charges + Advocate fees"
                  value={inputData.registration_charges}
                  onChange={handleInputChange}
                  disabled={!isCurrentStep}
                  min={0}
                />
              </div>
              <div className="row">
                <span className="label">Enter Project Completion ETA</span>
                <input
                  type="date"
                  name="completion_eta"
                  required
                  placeholder="DD/MM/YYYY"
                  value={inputData.completion_eta}
                  onChange={handleInputChange}
                  className="input"
                  min={new Date().toISOString().split("T")[0]}
                  disabled={!isCurrentStep}
                />
              </div>
              <div className="row">
                <span className="label">Interest on late payment (per day)</span>
                <div className="input_with_suffix_wrapper">
                  <input
                    type="number"
                    name="late_payment_interest"
                    required
                    className="input"
                    value={inputData.late_payment_interest}
                    onChange={handleInputChange}
                    disabled={!isCurrentStep}
                    min={0}
                    max={100}
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
              <div className="row">
                <span className="label">Days of grace (before late payment is charged)</span>
                <input
                  type="number"
                  name="days_of_grace"
                  required
                  className="input"
                  placeholder="Enter no. of days"
                  value={inputData.days_of_grace}
                  onChange={handleInputChange}
                  disabled={!isCurrentStep}
                  min={0}
                />
              </div>
              <div className="row">
                <span className="label">Limit number of plots agent can put on priority </span>
                <input
                  type="number"
                  name="plot_hold_limit"
                  required
                  className="input"
                  placeholder="Enter no."
                  value={inputData.plot_hold_limit}
                  onChange={handleInputChange}
                  disabled={!isCurrentStep}
                  min={0}
                />
              </div>
              <div className="row">
                <span className="label">Cancellation %</span>
                <div className="input_with_suffix_wrapper">
                  <input
                    type="number"
                    name="cancellation_percent"
                    required
                    className="input"
                    value={inputData.cancellation_percent}
                    onChange={handleInputChange}
                    disabled={!isCurrentStep}
                    min={0}
                    max={100}
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
            </div>
          </section>

          <div className="section_title_wrapper">
            <h3 className="section_title">Upload Related Documents</h3>
            <ToggleButton open={showDocumentsSection} onClick={() => setShowDocumentsSection((prev) => !prev)} />
          </div>
          {showDocumentsSection && (
            <div className="column">
              <div className="row">
                <span className="label">Brochure</span>
                {urls.brochure ? (
                  <Link target="_blank" to={urls.brochure} className="preview">
                    preview
                  </Link>
                ) : (
                  <span className="small gray">No Document Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleBrochureUpload}>
                  Upload
                </Button>
              </div>
              <div className="row">
                <span className="label">Project Layout</span>
                {urls.project_layout ? (
                  <Button
                    className="ghost_gray"
                    onClick={() => handleViewMedia("Project Layout", "image", "project_layout")}
                  >
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No image Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleProjectLayoutUpload}>
                  Upload
                </Button>
              </div>
              <div className="row">
                <span className="label">Location</span>
                {uploads.google_maps_url ? (
                  <Link target="_blank" to={uploads.google_maps_url} className="preview">
                    visit link
                  </Link>
                ) : (
                  <span className="small gray">No link uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={() => setShowLinkUploadModal(true)}>
                  Upload Link
                </Button>
              </div>
              <div className="row">
                <span className="label">Masterfile PDF</span>
                {urls.masterfile_pdf ? (
                  <Link target="_blank" to={urls.masterfile_pdf} className="preview">
                    preview
                  </Link>
                ) : (
                  <span className="small gray">No Document Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleMasterfileUpload}>
                  Upload
                </Button>
              </div>
              <div className="row">
                <span className="label">RERA Certificate</span>
                {urls.rera_certificate ? (
                  <Link target="_blank" to={urls.rera_certificate} className="preview">
                    preview
                  </Link>
                ) : (
                  <span className="small gray">No Document Uploaded</span>
                )}
                <Button className="upload" disabled={!isCurrentStep} onClick={handleReraCertificateUpload}>
                  Upload
                </Button>
              </div>
            </div>
          )}

          <div className="section_title_wrapper">
            <h3 className="section_title">Why invest in this project?</h3>
            <ToggleButton open={showDescriptionSection} onClick={() => setShowDescriptionSection((prev) => !prev)} />
          </div>
          {showDescriptionSection && (
            <div className="input_wrapper">
              <textarea
                name="description"
                required
                className="input_textarea"
                placeholder="Enter text"
                onChange={handleInputChange}
                value={inputData.description}
                disabled={!isCurrentStep}
                maxLength={600}
              />
              <span className="end small gray">{inputData.description?.length ?? 0}/600</span>
            </div>
          )}
          {isCurrentStep && (
            <SubmitButton form="new-project-form" end>
              SAVE
            </SubmitButton>
          )}
        </section>
      </FormWrapper>

      {showCreateCompanyModal &&
        createPortal(
          <CreateCompanyModal handleClose={handleCloseCreateCompanyModal} isVisible={showCreateCompanyModal} />,
          document.body
        )}

      {showLinkUploadModal &&
        createPortal(
          <LinkUploadModal
            handleClose={() => setShowLinkUploadModal(false)}
            isVisible={showLinkUploadModal}
            handleSave={handleSaveLink}
            data={uploads.google_maps_url}
            linkType="google_maps_url"
            invalidMessage="Please enter a valid google maps url"
          />,
          document.body
        )}

      {showImageUploadModal &&
        createPortal(
          <ImageUploadModal
            handleClose={() => setShowImageUploadModal(false)}
            isVisible={showImageUploadModal}
            {...fileUploadOptions}
          />,
          document.body
        )}

      {showFileUploadModal &&
        createPortal(
          <FileUploadModal
            handleClose={() => setShowFileUploadModal(false)}
            isModalVisible={showFileUploadModal}
            {...fileUploadOptions}
          />,
          document.body
        )}

      {showViewMediaModal &&
        createPortal(
          <ViewMediaModal
            handleClose={() => setShowViewMediaModal(false)}
            isModalVisible={showViewMediaModal}
            {...viewMediaOptions}
          />,
          document.body
        )}

      {showImageSliderModal &&
        createPortal(
          <ImageSliderModal
            handleClose={() => setShowImageSliderModal(false)}
            isModalVisible={showImageSliderModal}
            handleUpload={(ids, urls) => handleFileUpload(ids, urls, "slider_images")}
          />,
          document.body
        )}
    </>
  );
};
export default ProjectForm;
